import React from 'react';
import { Alert, Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const MIN_JAHR = 1970;
const MAX_JAHR = 2100;

export function jahrAusDatum(iso: string): number {
  const y = Number.parseInt(iso.slice(0, 4), 10);
  return Number.isFinite(y) ? y : NaN;
}

export function istAnderesJahr(von: string, selectedYear: number): boolean {
  const y = jahrAusDatum(von);
  return Number.isFinite(y) && y !== selectedYear;
}

/** Offene Anträge, deren Beginn nicht im gewählten Jahr liegt, nach Jahr gruppiert. */
export function offeneAntraegeNachJahr<T extends { von: string; status: string }>(
  antraege: T[],
  ausgewaehltesJahr: number,
): { jahr: number; anzahl: number }[] {
  const counts = new Map<number, number>();
  for (const a of antraege) {
    if (a.status !== 'Beantragt') continue;
    const y = jahrAusDatum(a.von);
    if (!Number.isFinite(y) || y === ausgewaehltesJahr) continue;
    counts.set(y, (counts.get(y) ?? 0) + 1);
  }
  const gruppen: { jahr: number; anzahl: number }[] = [];
  counts.forEach((anzahl, jahr) => {
    gruppen.push({ jahr, anzahl });
  });
  gruppen.sort((a, b) => a.jahr - b.jahr);
  return gruppen;
}

export function hinweisText(anzahl: number, jahr: number): string {
  if (anzahl === 1) return `1 Antrag in ${jahr} wartet`;
  return `${anzahl} Anträge in ${jahr} warten`;
}

interface UrlaubJahrSchalterProps {
  jahr: number;
  aktuellesJahr: number;
  onChange: (jahr: number) => void;
  prefix?: string;
}

export const UrlaubJahrSchalter: React.FC<UrlaubJahrSchalterProps> = ({
  jahr,
  aktuellesJahr,
  onChange,
  prefix = 'Urlaub',
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Typography variant="body2" component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
        {prefix} ·
      </Typography>
      <Button
        size="small"
        color="inherit"
        startIcon={<ChevronLeftIcon />}
        aria-label="Vorjahr"
        disabled={jahr <= MIN_JAHR}
        onClick={() => onChange(jahr - 1)}
        sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0 }}
      >
        Vorjahr
      </Button>
      <Typography
        variant="body2"
        component="span"
        aria-live="polite"
        sx={{ fontWeight: 700, minWidth: '3.2rem', textAlign: 'center', color: 'text.primary' }}
      >
        {jahr}
      </Typography>
      <Button
        size="small"
        color="inherit"
        endIcon={<ChevronRightIcon />}
        aria-label="Nächstes Jahr"
        disabled={jahr >= MAX_JAHR}
        onClick={() => onChange(jahr + 1)}
        sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0 }}
      >
        Nächstes Jahr
      </Button>
      {jahr !== aktuellesJahr && (
        <Button
          size="small"
          onClick={() => onChange(aktuellesJahr)}
          sx={{ textTransform: 'none', ml: 0.5 }}
        >
          Aktuelles Jahr
        </Button>
      )}
    </Box>
  );
};

interface HinweisOffeneAndereJahreProps {
  gruppen: { jahr: number; anzahl: number }[];
  onJahr: (jahr: number) => void;
}

export const HinweisOffeneAndereJahre: React.FC<HinweisOffeneAndereJahreProps> = ({
  gruppen,
  onJahr,
}) => {
  if (gruppen.length === 0) return null;
  return (
    <Alert severity="warning" sx={{ mb: 2, alignItems: 'center' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {gruppen.map((g) => (
          <Tooltip key={g.jahr} title={`Ansicht auf ${g.jahr} umschalten`}>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => onJahr(g.jahr)}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: 'background.paper' }}
            >
              {hinweisText(g.anzahl, g.jahr)}
            </Button>
          </Tooltip>
        ))}
      </Box>
    </Alert>
  );
};

export const UrlaubFremdJahrChip: React.FC<{ von: string; selectedYear: number }> = ({
  von,
  selectedYear,
}) => {
  if (!istAnderesJahr(von, selectedYear)) return null;
  const y = jahrAusDatum(von);
  return (
    <Tooltip title="Offener Antrag aus einem anderen Jahr">
      <Chip
        size="small"
        label={String(y)}
        variant="outlined"
        sx={{ ml: 1, fontWeight: 700, borderColor: '#e65100', color: '#e65100' }}
      />
    </Tooltip>
  );
};
